export type EmbryoViewMode = 'fresh' | 'frozen';

export interface ImplantSessionParams {
  impDate: string;
  siteId: string;
  yardId: string;
  vetId: string;
  techId: string;
  chuteNumber: number;
}

export interface LookupItem {
  id: string;
  name: string;
}

export interface Embryo {
  embryoId: string;
  isOutsideEmbryo?: boolean;
  stage: string;
  grade: string;
  comm: string;
  semenTypeName: string;
  name: string;
  clientRecip: string;
  calfTypeName: string;
  embryoTagNumber: string;
  tech: string;
  estrusSyncNumber: string;
  stateCode: string;
  embryoPartners: string;
  donorPartners: string;
  loadPartners: string;
  cl: string;
  plateNumber: string;
  dsImplantType: string;
  embImplantType: string;
  status: string;
  fertilizationId: string;
  freshStrawNumber: string;
  strawNumber: string;
  cane: string;
  freezeMethodId: string;
  tt: string;
  frozenStraw: string;
  recipId: string;
  methodName: string;
  fertSite: string;
  fertDate: string;
}

export interface Recipient {
  id: number;
  recipId: string;
  tag: string;
  recipStatusIdName: string;
  receivedDate: string | null;
  estrusSync: number | null;
  chuteNumber: number;
  stateCode: number;
  kickOuts: number;
  lastDrugUsed: string;
}

export interface RecipQueueItem {
  queueId: number;
  recipId: string;
  tag: string;
  recipStatusIdName: string;
  estrusSync: number | null;
  chuteNumber: number;
  kickOuts: number;
  lastDrugUsed: string;
  clientRecipTag: string | null;
}

export interface EmbryoOwner {
  ownerId: string;
  ownerRanchCode: string;
  ownerName: string;
}

export interface RecipProcedure {
  recipId: string;
  recipName: string;
  accountId: string;
  accountNumber: string;
  date: string;
  procCode: string;
  procCodeId: string;
  description: string;
  time: string;
  timeAMPM: string;
  preg: string;
  drugId: string | null;
  drugName: string;
}

export interface KickOutComment {
  commentId: string;
  commentName: string;
}

export type FreshEmbryoFilters = string[];

export const CALF_TYPE_FILTER_OPTIONS = [
  'Purchased Pregnancy',
  'Live Calf',
  'Client',
  'Clone',
  'Research',
  'Weaned Calf',
] as const;

export type ThawMethodOption = {
  value: number;
  label: string;
};

export interface DataEntryRecord {
  embryoId: string;
  isOutsideEmbryo?: boolean;
  mating: string;
  stage: string;
  grade: string;
  freshStrawNum: string;
  strawNum: string;
  embryoTagNumber: string;
  embryoOwnerId: string;
  embryoCane: string;
  embryoOwners: EmbryoOwner[];
  recipId: string;
  recipQueueId: number;
  recipName: string;
  recipStatus: string;
  recipSync: string;
  recipType: string;
  cl: string;
  recipProcs: RecipProcedure[];
  chuteNumber: number;
  transQual: number;
  transDist: number;
  recipProcDescription: string;
  kickOutComments: KickOutComment[];
  implantComment: string;
  impDate: string;
  siteId: string;
  yardId: string;
  vetId: string;
  techId: string;
  thawMethod: string;
  thawMethodId: number | null;
  thawMethodOptions: ThawMethodOption[];
  haulIn: boolean;
  partnership: boolean;
  partnershipAccounts: string;
  isFresh: boolean;
  billingAccountId: string;
  billingAccountName: string;
}

export interface ImplantEmbryoInput {
  embryoId: string;
  recipId: string | null;
  recipQueueId: number;
  yardId: string;
  siteId: string;
  vetId: string;
  techId: string;
  cl: string;
  transQual: number;
  transDist: number;
  comment: string;
  hcgApplied: boolean;
  keepInQueue: boolean;
  secEmbryo: boolean;
  usePartnership: boolean;
  haulIn: boolean;
  recipName: string | null;
  embryoOwnerId: string;
  impDate: string;
  chuteNumber: number;
  recipSync: string | null;
  recipType: string | null;
  thawMethod: string | null;
  embryoTagNumber: string | null;
  billingAccountId: string | null;
}

export interface HeatNotUsedInput {
  recipId: string;
  recipQueueId: number;
  yardId: string;
  vetId: string;
  techId: string;
  description: string;
  implantCidr: boolean;
  drugId: string;
  dose: string;
  unit: string;
  chuteNumber: number;
}

export interface CreateDonorInput {
  donorName: string;
  accountId: string;
}

export interface CreateSireInput {
  sireName: string;
  regNumber: string;
  breed: string;
  accountId: string;
}

export interface OutsideEmbryoInput {
  donorId: string;
  sireId: string;
  ownerId: string;
  breed: string;
  grade: string;
  stage: string;
  freezeMethodId: string;
  siteId: string;
  impDate: string;
}

export interface AddRecipToQueueInput {
  recipName: string;
  yardId: string;
  chuteNumber: number;
  clientRecip: boolean;
}

export interface PCInput {
  recipId: string;
  yardId: string;
  vetId: string;
  techId: string;
}

export interface ImplantCidrInput {
  recipId: string;
  yardId: string;
  vetId: string;
  techId: string;
}

export interface ChangeEmbryoOwnerInput {
  embryoId: string;
  newOwnerId: string;
}

export interface SearchResult {
  id: string;
  name: string;
}
